import type {
  Composition,
  HotspotConfigs,
} from '@spreadgroup/common-partner-lib/composition';
import type { HotspotKey } from '@spreadgroup/common-partner-lib/states';
import { printAreas } from './compositionHelper';

const PRINT_AREA_GROUPS = Object.keys(printAreas);

/** Below two designs this is an ordinary single-design creation. */
export const MIN_PRINT_AREA_DESIGNS = 2;

/** One design per print area. */
export const MAX_PRINT_AREA_DESIGNS = PRINT_AREA_GROUPS.length;

/**
 * Mirrors publishing-core's own defaults (`CompositionResetHandler`). Change `FRONT` and every
 * reset moves the design back. See `docs/publishing/multi-print-area-creation.md` §7.
 */
export const PRINT_AREA_DEFAULT_HOTSPOT: Record<string, HotspotKey> = {
  FRONT: 'MEDIUM_FRONT',
  BACK: 'FULL_BACK',
  LEFT: 'LEFT',
  RIGHT: 'RIGHT',
};

export const toHotspotConfigs = (
  placement: Record<string, number>,
): HotspotConfigs =>
  Object.fromEntries(
    Object.entries(placement).map(([group, designId]) => [
      PRINT_AREA_DEFAULT_HOTSPOT[group],
      [{ designId }],
    ]),
  );

export const getPrintAreaGroupOfHotspot = (
  hotspot: string,
): string | undefined =>
  PRINT_AREA_GROUPS.find((group) =>
    printAreas[group].hotspots.includes(hotspot as HotspotKey),
  );

export const getFreePrintAreaGroups = (
  hotspotConfigs?: HotspotConfigs,
): string[] => {
  const taken = new Set(
    Object.keys(hotspotConfigs || {})
      .map(getPrintAreaGroupOfHotspot)
      .filter((group): group is string => !!group),
  );

  return PRINT_AREA_GROUPS.filter((group) => !taken.has(group));
};

export const getHotspotOfDesign = (
  hotspotConfigs: HotspotConfigs | undefined,
  designId: number,
): string | undefined =>
  Object.entries(hotspotConfigs || {}).find(([, placed]) =>
    placed.some((config) => config.designId === designId),
  )?.[0];

/** No supported hotspots means "not narrowed", not "nothing supported". */
const supportedBy =
  (supportedHotspots?: HotspotKey[]) =>
  (hotspot: string): boolean =>
    !supportedHotspots?.length ||
    supportedHotspots.includes(hotspot as HotspotKey);

/**
 * The composition publishing-core just created, turned into the one the partner assembled. Carries
 * the whole created composition through so no seeded field is lost.
 */
export const withPrintAreaDesigns = ({
  composition,
  designIds,
  mainDesignId,
  hotspotConfigs,
}: {
  composition: Composition;
  designIds: number[];
  mainDesignId: number;
  hotspotConfigs: HotspotConfigs;
}): Composition => {
  if (!designIds.includes(mainDesignId)) {
    throw new Error(
      `Main design ${mainDesignId} is not among the selected designs`,
    );
  }

  const defaultHotspot = getHotspotOfDesign(hotspotConfigs, mainDesignId);
  if (!defaultHotspot) {
    throw new Error(`Main design ${mainDesignId} is not placed on a hotspot`);
  }

  const seeded = composition.templateProducts?.[0];

  return {
    ...composition,
    designs: designIds.map((id) => ({ id })),
    templateProducts: [
      {
        ...seeded,
        templateProduct: {
          ...seeded?.templateProduct,
          defaultHotspot,
          hotspotConfigs,
        },
      },
    ],
  } as Composition;
};

/**
 * `GET …/compositions/{id}/reset` rebuilds `templateProducts` around `mainDesign` only but leaves
 * `designs` untouched, stranding every other design on no hotspot at all. Only unplaced designs go
 * onto free print areas, and only as many as fit — a flexi-migrated composition can carry more
 * designs than there are print areas. See `docs/publishing/multi-print-area-creation.md` §11.
 */
export const withUnplacedDesignsRestored = (
  composition: Composition,
): Composition => {
  const seeded = composition.templateProducts?.[0];
  const hotspotConfigs = seeded?.templateProduct?.hotspotConfigs || {};

  const unplaced = (composition.designs || [])
    .map((design) => design.id)
    .filter((designId) => !getHotspotOfDesign(hotspotConfigs, designId));

  if (!unplaced.length) return composition;

  const restored = { ...hotspotConfigs };
  getFreePrintAreaGroups(hotspotConfigs).forEach((group, index) => {
    const designId = unplaced[index];
    if (designId) restored[PRINT_AREA_DEFAULT_HOTSPOT[group]] = [{ designId }];
  });

  const defaultHotspot =
    seeded?.templateProduct?.defaultHotspot ||
    getHotspotOfDesign(restored, composition.mainDesign.id) ||
    composition.defaultValues?.defaultHotspot;

  return {
    ...composition,
    templateProducts: [
      {
        ...seeded,
        templateProduct: {
          ...seeded?.templateProduct,
          defaultHotspot,
          hotspotConfigs: restored,
        },
      },
    ],
  } as Composition;
};

/**
 * A product type offers only some hotspots (`ProductType.hotspots`) and the editor renders nothing
 * for one it does not offer, so a design on an unsupported hotspot is moved — preferring its own
 * print area, then any free one. It is dropped only when no print area is left.
 */
export const narrowHotspotConfigsToProductType = ({
  hotspotConfigs,
  supportedHotspots,
}: {
  hotspotConfigs?: HotspotConfigs;
  supportedHotspots?: HotspotKey[];
}): HotspotConfigs => {
  if (!hotspotConfigs) return {};

  const supports = supportedBy(supportedHotspots);
  const firstSupportedHotspotOf = (group: string) =>
    printAreas[group].hotspots.find(supports);

  const narrowed: HotspotConfigs = {};
  const unsupported: { hotspot: string; designs: HotspotConfigs[string] }[] =
    [];

  Object.entries(hotspotConfigs).forEach(([hotspot, designs]) => {
    const group = getPrintAreaGroupOfHotspot(hotspot);

    // A print area holds at most one design; the input can break that and must not carry it in.
    if (group && !getFreePrintAreaGroups(narrowed).includes(group)) return;

    if (supports(hotspot)) {
      narrowed[hotspot] = designs;
    } else {
      unsupported.push({ hotspot, designs });
    }
  });

  unsupported.forEach(({ hotspot, designs }) => {
    const free = getFreePrintAreaGroups(narrowed);
    const ownGroup = getPrintAreaGroupOfHotspot(hotspot);

    const target =
      (ownGroup && free.includes(ownGroup)
        ? firstSupportedHotspotOf(ownGroup)
        : undefined) ||
      free.map(firstSupportedHotspotOf).find((candidate) => !!candidate);

    if (target) narrowed[target] = designs;
  });

  return narrowed;
};

/**
 * Not `compositionHelper.getHotspotConfigs`, which reads `templateProducts[0]` — a SMART assumption.
 * In ADVANCED index 0 is just as likely to be some product type's own placement.
 */
export const getGenericHotspotConfigs = (
  composition: Composition,
): HotspotConfigs | undefined =>
  composition.templateProducts?.find(
    (templateProduct) =>
      !('productTypeIds' in templateProduct) ||
      !templateProduct.productTypeIds?.length,
  )?.templateProduct?.hotspotConfigs;

/**
 * The placement a product type's settings open with: inherit the generic one where the product type
 * can print it, else the main design on the product type's default hotspot, then fill whatever is
 * still unplaced onto a free print area.
 */
export const seedHotspotConfigsForProductType = ({
  composition,
  supportedHotspots,
  defaultHotspot,
}: {
  composition: Composition;
  supportedHotspots?: HotspotKey[];
  defaultHotspot?: string;
}): HotspotConfigs => {
  const narrow = (hotspotConfigs?: HotspotConfigs) =>
    narrowHotspotConfigsToProductType({ hotspotConfigs, supportedHotspots });

  const inherited = narrow(getGenericHotspotConfigs(composition));

  const seeded = Object.keys(inherited).length
    ? inherited
    : narrow(
        defaultHotspot
          ? { [defaultHotspot]: [{ designId: composition.mainDesign.id }] }
          : undefined,
      );

  const supports = supportedBy(supportedHotspots);

  (composition.designs || [])
    .map((design) => design.id)
    .filter((designId) => !getHotspotOfDesign(seeded, designId))
    .forEach((designId) => {
      const target = getFreePrintAreaGroups(seeded)
        .map((group) => printAreas[group].hotspots.find(supports))
        .find((candidate) => !!candidate);

      if (target) seeded[target] = [{ designId }];
    });

  return seeded;
};
